import type { LiteClient } from 'algoliasearch/lite'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { TODAY_START, WEEK_START } from '../../../../assets/constants/datetime'

const LIMIT_DATE = dayjs().add(2, 'month').startOf('day')

/** Number of weeks (from today) the compact view lists at once. */
export const COMPACT_VIEW_WEEKS = 4

const WEEK_TIMEFRAME = [
  WEEK_START.unix() * 1000,
  WEEK_START.endOf('isoWeek').unix() * 1000
]

const COMPACT_TIMEFRAME = [
  TODAY_START.unix() * 1000,
  TODAY_START.add(COMPACT_VIEW_WEEKS, 'week').endOf('day').unix() * 1000
]

/*
const safetyCountLimit = 50

function useDetermineDateFilterOld(
  searchClient: LiteClient,
  indexName: string
) {
  const [dateFilter, setDateFilter] = useState<number[] | null>(null)

  useEffect(() => {
    let continueQuery = true
    let timeFrame: [Dayjs, Dayjs] = [WEEK_START, WEEK_END]
    const query = async () => {
      let count = 0
      while (
        continueQuery &&
        timeFrame[0]?.isBefore(LIMIT_DATE) &&
        count < safetyCountLimit
      ) {
        const { results } = await searchClient.searchForFacets({
          requests: [
            {
              indexName,
              type: 'facet',
              facet: 'date',
              filters: `date:${timeFrame[0].unix() * 1000} TO ${timeFrame[1].unix() * 1000}`
            }
          ]
        })
        const hits = results[0]?.facetHits
        if (hits && hits.length > 0) {
          continueQuery = false
          setDateFilter([
            timeFrame[0].unix() * 1000,
            timeFrame[1].unix() * 1000
          ])
        } else {
          const newStart = timeFrame[0].add(1, 'week').startOf('isoWeek')
          const newEnd = timeFrame[1].add(1, 'week').endOf('isoWeek')
          timeFrame = [
            newStart,
            newEnd.isAfter(LIMIT_DATE) ? LIMIT_DATE : newEnd
          ]
        }
        count++
        if (count >= safetyCountLimit) {
          console.warn('Safety count limit reached')
        }
      }
    }
    query()

    return () => {
      continueQuery = false
    }
  }, [searchClient, indexName])

  return dateFilter
}
*/

async function fetchTimeframeWithHits(searchClient: LiteClient, indexName: string, searchMinDate: number, searchMaxDate: number, abortSignal?: AbortSignal) {
  const { results } = await searchClient.searchForFacets({
    requests: [
      {
        indexName,
        type: 'facet',
        facet: 'date',
        filters: `date:${searchMinDate} TO ${searchMaxDate}`
      }
    ]
  })

  if (abortSignal && abortSignal.aborted) {
    return
  }

  console.log(results)

  const hits = results[0]?.facetHits

  if (hits && hits.length > 0) {
    const sortedByTimestamp = hits
      .map((h) => Number.parseInt(h.value))
      .filter(Boolean)
      .sort()
    const timestamp = sortedByTimestamp[0]
    const date = dayjs(timestamp)
    return [
      date.startOf('isoWeek').unix() * 1000,
      date.endOf('isoWeek').unix() * 1000
    ]
  }

  return null
}

/**
 * Week mode: starts on the current ISO week and jumps to the first upcoming
 * week that has celebrations. Compact mode: fixed range from today until
 * COMPACT_VIEW_WEEKS weeks ahead, without the lookup query.
 */
function useDetermineDateFilter(
  searchClient: LiteClient,
  indexName: string,
  compactView = false
) {
  const state = useState<number[] | null>(
    compactView ? COMPACT_TIMEFRAME : WEEK_TIMEFRAME
  )
  const setDateFilter = state[1]

  useEffect(() => {
    // Reset when the mode is toggled (block inspector), initial state only applies on mount
    setDateFilter(compactView ? COMPACT_TIMEFRAME : WEEK_TIMEFRAME)
    if (compactView) {
      return
    }

    const abortController = new AbortController()
    const query = async () => {
      const timeframe = await fetchTimeframeWithHits(searchClient, indexName, WEEK_START.unix() * 1000, LIMIT_DATE.unix() * 1000, abortController.signal)
      if (timeframe) {
        setDateFilter(timeframe)
      }
    }

    query()

    return () => {
      abortController.abort()
    }
  }, [searchClient, indexName, compactView])

  return state
}

export default useDetermineDateFilter
